// Ported verbatim from nenda-autoplayer's src/lib/utils/schedule.ts (the
// Schedule-lookup helpers) + the one function `getCurrentContentPlaylistId`
// needs from src/lib/utils/time.ts (`getLocalDateTimeAsZulu`), consolidated
// into one file since guards.ts only needs the schedule-lookup side.
//
// `getLocalDateTimeAsZulu` uses the DEVICE's own ambient system timezone
// (`new Date().getTimezoneOffset()`), not the premise's configured IANA zone
// Foyer's server-side `toWallClockZ` (legacySignageSchedule.ts) uses when
// BUILDING the same wall-clock-Z keys this reads back. Ported faithfully,
// not redesigned: this is upstream's own existing, battle-tested mechanism
// for resolving "which schedule key is active right now" client-side, and a
// signage kiosk's system clock/timezone should already be provisioned to
// match its premise (the same assumption Foyer's own device pairing makes
// elsewhere). Worth re-verifying against real device behaviour once this
// engine is actually running on hardware, not a reason to hold up the port.
import type {
  Schedule,
  SignagePlaylist,
  ScheduleItem,
} from "../types/Schedule";
import { skewedNow } from "./clockSkew";

/**
 * Finds the closest key in the map that is before the current time.
 * Assumes that the keys are valid date strings.
 *
 * DELTA FROM UPSTREAM: reads `skewedNow()` (see clockSkew.ts) instead of a
 * bare `new Date()` - defaults to the exact same value (0ms skew is a
 * no-op), and lets Foyer glue correct for TV clock drift the same way
 * SignageSurface.tsx already does, without touching this function's own
 * signature or its device-timezone handling below.
 */
export const getClosestKeyBeforeCurrentTime = (
  scheduleMap: Record<string, ScheduleItem>
): string | undefined => {
  const localNowAsZulu = getLocalDateTimeAsZulu(skewedNow());

  const keysBeforeNow = Array.from(Object.keys(scheduleMap)).filter(
    (key) => new Date(key).getTime() <= localNowAsZulu.getTime()
  );
  if (keysBeforeNow.length === 0) return undefined;
  keysBeforeNow.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  return keysBeforeNow[0];
};

/** Retrieves the current content playlist ID based on the closest schedule
 *  item before the current time. */
export const getCurrentContentPlaylistId = (
  schedule: Schedule
): string | undefined => {
  const activeScheduleKey = getClosestKeyBeforeCurrentTime(
    schedule.contentSchedule
  );

  if (!activeScheduleKey) return undefined;

  return schedule.contentSchedule[activeScheduleKey]!.playlist;
};

/** Checks if a channel with the specified ID exists in the schedule. */
export const hasChannel = (schedule: Schedule, channelId: string): boolean =>
  schedule.channels.some((channel) => channel.id === channelId);

/** Retrieves a specific signage playlist by its ID from the schedule. */
export const getSignagePlaylist = (
  schedule: Schedule,
  playlistId: string
): SignagePlaylist | undefined =>
  schedule.signagePlaylists.find((playlist) => playlist.id === playlistId);

export const getLocalDateTimeAsZulu = (date: Date): Date => {
  const offset = new Date().getTimezoneOffset();
  const adjusted = date.getTime() - offset * 60 * 1000;
  return new Date(adjusted);
};

/** Also from utils/time.ts - `evaluateSchedule` (actions.ts) uses this to
 *  build the exact key format `slotSchedule` is keyed by (seconds truncated,
 *  not rounded - matching `toWallClockZ`'s own documented contract). */
export const getTruncatedTimeString = (dateTime: Date): string => {
  const dateAsZulu = getLocalDateTimeAsZulu(dateTime);
  return new Date(
    dateAsZulu.getFullYear(),
    dateAsZulu.getMonth(),
    dateAsZulu.getDate(),
    dateAsZulu.getHours(),
    dateAsZulu.getMinutes(),
    dateAsZulu.getSeconds()
  ).toISOString();
};
